import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GameService } from '../../core/state/game.service';
import { calculateAttendance } from '../../core/simulation/simulation';
import { USER_CLUB_ID } from '../../../data/initialData';
import { PlayerEditorialRating } from '../../core/models/types';
import { CoachMatchBonus } from '../../core/models/coaches.types';
import { IconComponent } from '../../shared/ui/icon/icon.component';

@Component({
  selector: 'app-match-engine',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './match-engine.component.html',
  styleUrl: './match-engine.component.scss'
})
export class MatchEngineComponent {
  readonly game = inject(GameService);

  activeSubTab: 'editorial' | 'engine' = 'editorial';

  /**
   * Entrenador congelado al inicio de la jornada según live/snapshot del motor de partidos.
   * Ausencia de entrenador o datos antiguos equivalen a mejora cero (+0%).
   * Las potencias del motor ya incorporan la mejora.
   */
  frozenCoach(): CoachMatchBonus | null {
    const fix = this.userFixture();
    if (!fix) return null;
    const uClub = this.game.userClub();
    const isHome = fix.homeClubId === uClub?.id;
    const rawCoach = isHome
      ? (fix as any).homeCoach || (fix.engineCalculation as any)?.homeCoach
      : (fix as any).awayCoach || (fix.engineCalculation as any)?.awayCoach;

    if (rawCoach && (rawCoach.name || rawCoach.coachId)) {
      return {
        coachId: rawCoach.coachId || null,
        name: rawCoach.name || 'Entrenador Congelado',
        attack: Number(rawCoach.attack) || 0,
        midfield: Number(rawCoach.midfield) || 0,
        defense: Number(rawCoach.defense) || 0,
      };
    }
    return null;
  }

  isScored(): boolean {
    const s = this.game.currentMatchday()?.status;
    return s === 'SCORED' || s === 'CLOSED';
  }

  userFixture() {
    const uClub = this.game.userClub();
    return this.game.currentMatchday()?.fixtures?.find(
      (f) => f.homeClubId === uClub?.id || f.awayClubId === uClub?.id
    );
  }

  getClubName(clubId: string): string {
    const club = this.game.clubs().find((c) => c.id === clubId);
    return club ? club.name : 'Club Rival';
  }

  staffModPercent(): string {
    const club = this.game.userClub();
    const avg = (club.staff.goalkeeping + club.staff.fitness + club.staff.technical + club.staff.shooting) / 4;
    return (avg * 1.0).toFixed(1);
  }

  attendanceModPercent(): string {
    const club = this.game.userClub();
    const att = calculateAttendance(club.stadium, club.recentForm);
    return ((att.percentage - 50) * 0.1).toFixed(1);
  }

  momentumModPercent(): string {
    let m = 0;
    this.game.userClub().recentForm.slice(-5).forEach((r) => {
      if (r === 'W') m += 1.0;
      else if (r === 'D') m += 0.5;
    });
    return Math.min(5.0, m).toFixed(1);
  }

  updateStars(rate: PlayerEditorialRating, baseRating: number) {
    this.game.updateEditorialRating(rate.playerId, { baseRating });
  }

  changeGoals(rate: PlayerEditorialRating, delta: number) {
    const next = Math.max(0, rate.goals + delta);
    this.game.updateEditorialRating(rate.playerId, { goals: next });
  }

  changeYellow(rate: PlayerEditorialRating, delta: number) {
    const next = Math.max(0, Math.min(2, rate.yellowCards + delta));
    this.game.updateEditorialRating(rate.playerId, { yellowCards: next });
  }

  toggleRed(rate: PlayerEditorialRating) {
    const next = rate.redCards > 0 ? 0 : 1;
    this.game.updateEditorialRating(rate.playerId, { redCards: next });
  }

  calculatePreviewPoints(rate: PlayerEditorialRating): number {
    return rate.baseRating + rate.goals * 3 - rate.yellowCards * 1 - rate.redCards * 3;
  }
}
